import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import copy from 'copy-to-clipboard';
import PropTypes from 'prop-types';
import React from 'react';
import {useDrag} from 'react-dnd';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import {fitExtent} from '@explorer/actions/map';
import {selectMosaic} from '@explorer/actions/mosaics';
import {showModal} from '@explorer/actions/ui';
import {trackEvent} from '@explorer/analytics';
import {
  EyeBadgeIcon,
  ToggleButton,
} from '@explorer/components/items-panel/item-components';
import {THUMBNAIL_DIMENSIONS_V2} from '@explorer/components/items-panel/styles';
import CopyButton from '@explorer/components/ui/copy-button';
import ScrollingTitle from '@explorer/components/ui/scrolling-title';
import ThumbnailCard from '@explorer/components/ui/thumbnail-card';
import ThumbnailImage from '@explorer/components/ui/thumbnail-image';
import {COMPARE_TYPES, MODALS, UI_MODES} from '@explorer/constants';
import formatMosaicDate from '@explorer/util/format-mosaic-date';
import {calcEffectiveExtent, rulesForMosaic} from '@explorer/util/mosaics';
import {isCompareSubject} from '@explorer/util/results';

const MosaicListItemv2 = ({mosaic}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const basemapRules = useSelector(state => state.basemapRules);
  const isComparing = useSelector(state => state.ui.mode === UI_MODES.COMPARE);
  const isComparingSubject = useSelector(state =>
    isCompareSubject(state.compare.subjects, mosaic.id)
  );
  const isViewSelected = useSelector(
    state => state.mosaics.selected?.id === mosaic.id
  );

  const [{isDragging}, dragRef] = useDrag(
    () => ({
      type: COMPARE_TYPES.MOSAIC,
      item: mosaic,
      canDrag: isComparing && mosaic.hasAccess,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [isComparing]
  );

  const onViewButtonClick = () => {
    if (isViewSelected) {
      dispatch(selectMosaic(null));
      trackEvent('Mosaics: Hide item');
    } else {
      dispatch(selectMosaic(mosaic.id));
      trackEvent('Mosaics: Show item');
    }
  };

  const onRequestAccessClick = () => {
    trackEvent('Mosaics: Requested Access', {
      mosaicId: mosaic.id,
    });
    dispatch(showModal(MODALS.BASEMAPS_NO_ACCESS));
  };

  const handleClickBasemapsLink = () => {
    trackEvent('Redirected to Basemaps Viewer', {
      mosaicName: mosaic.name,
    });
  };

  const handleMosaicNameCopy = () => {
    trackEvent('Mosaics: copied basemap name', {
      name: mosaic.name,
    });
    copy(mosaic.name);
  };

  const onZoomToBasemapClick = () => {
    const rules = rulesForMosaic(basemapRules, mosaic.name);
    const extent = calcEffectiveExtent(mosaic, rules);

    dispatch(fitExtent(extent));
    trackEvent('Mosaics: Zoom to basemap');
  };

  if (!mosaic) {
    return false;
  }

  return (
    <Card
      ref={dragRef}
      sx={{
        cursor: isComparing ? (!isDragging ? 'grab' : 'grabbing') : 'default',
        transform: 'translate3d(0, 0, 0)',
        backgroundColor:
          isComparingSubject && isComparing
            ? 'primary.lighter'
            : 'background.paper',
        display: 'flex',
        paddingLeft: 1,
        paddingRight: 1.5,
        paddingY: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
      square
      elevation={0}
      draggable={isComparing && mosaic.hasAccess}
      data-qe="mosaic-list-item"
    >
      <ThumbnailCard
        button={!isComparing && mosaic.hasAccess}
        selected={!isComparing && isViewSelected}
        onClick={onViewButtonClick}
        aria-label={
          isViewSelected
            ? t('mosaicListItem.removeFromMap')
            : t('mosaicListItem.addToMap')
        }
        thumbnail={
          <ThumbnailImage
            altText={t('mosaicListItem.thumbnailLabel', {
              mosaicName: mosaic.name,
            })}
            src={mosaic._links.thumb}
            sx={{
              width: THUMBNAIL_DIMENSIONS_V2,
              height: THUMBNAIL_DIMENSIONS_V2,
              flexShrink: 0,
            }}
          />
        }
        data-qe="view-on-map-button"
      >
        {mosaic.hasAccess && !isComparing && (
          <EyeBadgeIcon isViewSelected={isViewSelected} />
        )}
      </ThumbnailCard>
      <Box
        sx={{
          marginLeft: 1,
          minWidth: 0,
          width: '100%',
        }}
      >
        <Box>
          <Box
            direction="row"
            sx={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="subtitle1" data-qe="mosaic-date-label">
                {t(...formatMosaicDate(mosaic))}
              </Typography>
              <Tooltip title={t('mosaicListItem.tooltipViewBasemap')}>
                <IconButton
                  size="small"
                  href={`https://www.planet.com/basemaps/#/mosaic/${mosaic.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClickBasemapsLink}
                  data-qe="basemap-viewer-button"
                  sx={{borderRadius: 1, padding: 0.25, marginLeft: 0.5}}
                >
                  <OpenInNewOutlinedIcon fontSize="small" sx={{fontSize: 18}} />
                </IconButton>
              </Tooltip>
            </Box>
            {mosaic.hasAccess && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: 'flex-end',
                  marginLeft: 'auto',
                }}
              >
                <Tooltip title={t('mosaicListItem.zoomToBasemap')}>
                  <ToggleButton
                    value="zoomToBasemap"
                    onClick={onZoomToBasemapClick}
                    data-qe="zoom-to-basemap-button"
                  >
                    <GpsFixedIcon fontSize="small" />
                  </ToggleButton>
                </Tooltip>
              </Stack>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '90%',
            }}
          >
            <ScrollingTitle
              align="left"
              variant="body2"
              data-qe="mosaic-description"
            >
              {mosaic.name}
            </ScrollingTitle>
            <CopyButton
              onCopy={handleMosaicNameCopy}
              size="small"
              textToCopy={mosaic.name}
              title={t('sidebarsBasemap.copyTooltipTypeBasemapName')}
              buttonStyleOverrides={{marginRight: 0.5, padding: 0.5}}
              data-qe="copy-button"
            />
          </Box>
        </Box>
        {!mosaic.hasAccess && (
          <Stack
            sx={{
              justifyContent: 'flex-end',
              marginTop: 1,
              flexGrow: 0,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={onRequestAccessClick}
              sx={{marginLeft: 'auto'}}
            >
              Purchase Access
            </Button>
          </Stack>
        )}
      </Box>
    </Card>
  );
};

MosaicListItemv2.propTypes = {
  mosaic: PropTypes.object.isRequired,
};

export default MosaicListItemv2;
